import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";
import moment from "moment";
import "moment/locale/fr";
import { useParams, useNavigate } from "react-router-dom";

moment.locale("fr");

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  refused: "Refusée",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  refused: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_OPTIONS = Object.keys(STATUS_LABELS);

export default function OfferDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [author, setAuthor] = useState(null);
  const [client, setClient] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchOffer() {
      setLoading(true);
      const offerSnap = await getDoc(doc(db, "offers", id));
      if (!offerSnap.exists()) {
        setLoading(false);
        return;
      }
      const offerData = offerSnap.data();
      setOffer(offerData);
      setNewStatus(offerData.status);

      if (offerData.authorId) {
        const userSnap = await getDoc(doc(db, "users", offerData.authorId));
        if (userSnap.exists()) setAuthor(userSnap.data());
      }

      if (offerData.userId) {
        const clientSnap = await getDoc(doc(db, "users", offerData.userId));
        if (clientSnap.exists()) setClient(clientSnap.data());
      }

      setLoading(false);
    }
    fetchOffer();
  }, [id]);

  const ProfilePhoto = ({ src, alt, name }) => {
    if (src)
      return (
        <img
          src={src}
          alt={alt}
          className="w-20 h-20 rounded-full object-cover ring-4 ring-gray-100"
        />
      );
    return (
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#801111] to-[#5a0c0c] flex items-center justify-center text-white font-bold text-2xl">
        {name?.[0]?.toUpperCase() || "?"}
      </div>
    );
  };

  const handleStatusChange = async (e) => {
    const val = e.target.value;
    setNewStatus(val);
    setSaving(true);
    try {
      await updateDoc(doc(db, "offers", id), { status: val });
      setOffer((prev) => ({ ...prev, status: val }));

      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Statut mis à jour avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
    } catch {
      alert("Erreur lors de la mise à jour du statut");
      setNewStatus(offer.status);
    }
    setSaving(false);
  };

  if (!offer)
    return (
      <PageTemplate
        loading={loading}
        content={
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                    />
                  </svg>
                </div>
                <p className="text-gray-600 font-medium">Offre introuvable</p>
                <button
                  onClick={() => navigate("/offers")}
                  className="mt-4 text-[#801111] hover:text-[#5a0c0c] font-medium"
                >
                  ← Retour à la liste des offres
                </button>
              </CardContent>
            </Card>
          </div>
        }
      />
    );

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Back Button */}
          <button
            onClick={() => navigate("/offers")}
            className="flex items-center gap-2 text-[#801111] hover:text-[#5a0c0c] font-medium transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            Retour à la liste des offres
          </button>

          {/* Header Card */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
            <CardContent className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#801111] rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-white"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-lg">
                    Détails de l'offre
                  </h3>
                  <p className="text-sm text-gray-600">
                    Créée le{" "}
                    {offer.createdAt && offer.createdAt.toDate
                      ? moment(offer.createdAt.toDate()).format("LL")
                      : "-"}
                  </p>
                </div>
              </div>
              <span
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${
                  STATUS_COLORS[offer.status] ||
                  "bg-gray-100 text-gray-800 border-gray-200"
                }`}
              >
                {STATUS_LABELS[offer.status] || offer.status}
              </span>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Author Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-[#801111]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                  Couturier (Auteur de l'offre)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {author ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
                      <ProfilePhoto
                        src={author.photo}
                        alt="Photo auteur"
                        name={author.firstname}
                      />
                      <div>
                        <h4 className="font-semibold text-lg text-gray-900">
                          {author.firstname} {author.lastname}
                        </h4>
                        <p className="text-sm text-gray-500">
                          @{author.username || "non défini"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Email
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {author.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Téléphone
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {author.phoneNumber || "-"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Adresse
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {author.address || "-"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Dernière connexion
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {author.lastLogin && author.lastLogin.toDate
                              ? moment(author.lastLogin.toDate()).fromNow()
                              : "-"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Équipement mobile
                      </p>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            author.mobileSerger ? "bg-green-500" : "bg-gray-300"
                          }`}
                        ></div>
                        <span className="text-sm text-gray-700">
                          Surjeteuse mobile
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            author.mobileSewingMachine
                              ? "bg-green-500"
                              : "bg-gray-300"
                          }`}
                        ></div>
                        <span className="text-sm text-gray-700">
                          Machine à coudre mobile
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center py-8 text-red-600 italic">
                    Auteur inconnu
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Client Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-[#801111]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                  Client
                </CardTitle>
              </CardHeader>
              <CardContent>
                {client ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
                      <ProfilePhoto
                        src={client.photo}
                        alt="Photo client"
                        name={client.firstname}
                      />
                      <div>
                        <h4 className="font-semibold text-lg text-gray-900">
                          {client.firstname} {client.lastname}
                        </h4>
                        <p className="text-sm text-gray-500">
                          @{client.username || "non défini"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Email
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {client.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Téléphone
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {client.phoneNumber || "-"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Adresse
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {client.address || "-"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Équipement personnel
                      </p>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            client.serger ? "bg-green-500" : "bg-gray-300"
                          }`}
                        ></div>
                        <span className="text-sm text-gray-700">
                          Possède une surjeteuse
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            client.sewingMachine ? "bg-green-500" : "bg-gray-300"
                          }`}
                        ></div>
                        <span className="text-sm text-gray-700">
                          Possède une machine à coudre
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center py-8 text-red-600 italic">
                    Client inconnu
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Offer Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Détails de l'offre</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {offer.message || "-"}
                  </p>
                </div>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix proposé
                </label>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-[#801111]">
                    {offer.price ?? "-"}
                  </span>
                  {offer.price && (
                    <span className="text-xl text-gray-500">€</span>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Statut de l'offre
                </label>
                <select
                  id="status"
                  value={newStatus}
                  onChange={handleStatusChange}
                  disabled={saving}
                  className="w-full md:w-auto border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Modifier le statut de l'offre"
                >
                  {STATUS_OPTIONS.map((key) => (
                    <option key={key} value={key}>
                      {STATUS_LABELS[key]}
                    </option>
                  ))}
                </select>
                {saving && (
                  <p className="text-sm text-gray-500 mt-2">
                    Sauvegarde en cours...
                  </p>
                )}
              </div>

              {/* Project ID */}
              {offer.projectId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID du projet
                  </label>
                  <code className="block bg-gray-100 px-3 py-2 rounded font-mono text-sm text-gray-800">
                    {offer.projectId}
                  </code>
                </div>
              )}

              {/* Created At */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de création
                </label>
                <p className="text-gray-900">
                  {offer.createdAt && offer.createdAt.toDate
                    ? moment(offer.createdAt.toDate()).format("LLLL")
                    : offer.createdAt
                    ? moment(offer.createdAt).format("LLLL")
                    : "-"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}